import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { settlementService } from '@/services/settlementService';
import { formatCurrency } from '@/lib/utils';
import { SettlementCalculationResult } from '@/engine/settlement_calculator';

interface SettlementFreezeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  settlementId: string;
  cycleId: string;
  cycleCode: string;
  growerName: string;
  calculationResult: SettlementCalculationResult;
  criticalIssues: any[];
}

export const SettlementFreezeModal: React.FC<SettlementFreezeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  settlementId,
  cycleId,
  cycleCode,
  growerName,
  calculationResult,
  criticalIssues,
}) => {
  const [confirmedVerified, setConfirmedVerified] = useState<boolean>(false);
  const [adminOverride, setAdminOverride] = useState<boolean>(false);
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const hasCriticalIssues = criticalIssues && criticalIssues.length > 0;

  const handleFreeze = async () => {
    if (!confirmedVerified) {
      setError('You must check the confirmation box indicating all operational data and adjustments are verified.');
      return;
    }

    if (hasCriticalIssues && (!adminOverride || !overrideReason.trim())) {
      setError('Unresolved critical reconciliation issues exist. Admin override with a mandatory justification reason is required to freeze.');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const res = await settlementService.freezeSettlementAtomic({
        settlementId,
        cycleId,
        calculationResult,
        adminOverride,
        overrideReason: overrideReason.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      if (!res.success) {
        throw new Error(res.error || 'Failed to freeze settlement');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred during settlement finalization.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-6">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-rose-900">
            <Lock className="w-5 h-5 text-rose-600" />
            <DialogTitle className="text-base font-bold">Finalize Settlement (Permanent Lock)</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2 text-xs">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md flex items-start gap-2 text-rose-700">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Warning Banner */}
          <div className="p-3.5 bg-rose-50/80 border border-rose-200 rounded-lg space-y-2 text-rose-950">
            <div className="font-bold flex items-center gap-1.5 text-rose-900">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>Database Immutability Trigger Notice</span>
            </div>
            <p className="leading-relaxed text-[11px]">
              Upon finalization, the PostgreSQL database trigger <code className="font-mono bg-rose-100 px-1 py-0.5 rounded text-rose-900">trg_enforce_settlement_immutability</code> will permanently lock this settlement, all itemized lines, and adjustment records against future edits or deletions.
            </p>
            <p className="leading-relaxed text-[11px]">
              The associated Production Cycle (<span className="font-semibold">{cycleCode}</span>) will automatically transition to status <code className="font-mono bg-indigo-100 text-indigo-900 px-1 py-0.5 rounded font-bold">Finalized</code>.
            </p>
          </div>

          {/* Financial Summary */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500">Cycle / Grower:</span>
              <span className="font-bold text-slate-800">{cycleCode} ({growerName})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total Sales Revenue:</span>
              <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.total_sales_revenue)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total Production Expenses:</span>
              <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.total_production_expenses)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Grower Base Share:</span>
              <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.grower_gross_share)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Net Adjustments:</span>
              <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.net_adjustments)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1">
              <span className="font-bold text-slate-900">Final Net Payout to Grower:</span>
              <span className="font-mono font-bold text-base text-rose-700">{formatCurrency(calculationResult.grower_net_payout)}</span>
            </div>
          </div>

          {/* Critical Issues Check */}
          {hasCriticalIssues && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <div className="flex items-center gap-1.5 font-bold text-amber-900">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>{criticalIssues.length} Unresolved Critical Issue(s)</span>
              </div>
              <ul className="list-disc pl-4 space-y-1 text-amber-900 text-[11px]">
                {criticalIssues.map((issue) => (
                  <li key={issue.id}>
                    <span className="font-semibold">{issue.issue_type}:</span> {issue.description}
                  </li>
                ))}
              </ul>
              <div className="pt-2 border-t border-amber-200 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminOverride}
                    onChange={(e) => setAdminOverride(e.target.checked)}
                    className="mt-0.5 rounded border-amber-300 text-rose-600 focus:ring-rose-500"
                  />
                  <span className="font-semibold text-amber-950">
                    Administrator Override: Proceed to freeze despite open critical issues.
                  </span>
                </label>
                {adminOverride && (
                  <div>
                    <label className="block text-[11px] font-semibold text-amber-950 mb-1">
                      Override Reason <span className="text-rose-600">*</span>
                    </label>
                    <textarea
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Mandatory justification for freezing with unresolved issues..."
                      rows={2}
                      className="w-full px-2.5 py-1.5 rounded border border-amber-300 bg-white text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Explicit Verification Checkbox */}
          <div className="p-3 bg-indigo-50/70 border border-indigo-200 rounded-lg">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmedVerified}
                onChange={(e) => setConfirmedVerified(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="font-semibold text-indigo-950 text-xs leading-relaxed">
                I confirm all pig scale weights, feed bag deliveries & usages, health events, operating expenses, contract profit split, and post-profit adjustments have been thoroughly audited and verified.
              </span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Final Settlement Remarks (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add final liquidation audit remarks or cheque / disbursement vouchers..."
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-xs text-slate-800 focus:ring-2 focus:ring-rose-500 focus:outline-none"
            />
          </div>
        </div>

        <DialogFooter className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="text-xs">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleFreeze}
            disabled={loading || !confirmedVerified || (hasCriticalIssues && (!adminOverride || !overrideReason.trim()))}
            className="text-xs bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-sm"
          >
            {loading ? 'Finalizing Settlement...' : '🔒 CONFIRM & FINALIZE SETTLEMENT'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
